/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useSelector } from "react-redux";
import { toast } from 'react-toastify';
import { Button } from "@mui/material";
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import customStyles from "../../../../../assets/styles/customStyles";
import MainHeader from "../../../../Shared/components/Header";
import SalesIcon from "../../../../../assets/images/icon_menus/SalesIcon.json";
import PrimaryCard from "../../../../Shared/components/Card/PrimaryCard";
import { CustomModal } from "../../../../Shared/components/DeleteModal";
import { checkViewPermission, checkCRUDPermission, idNamingConversion } from '../../../../../helpers/utilities';
import { postRequest } from "../../../../../helpers/axiosClient";
import useStyles from "./styles";
import { InvoiceInput } from "./InvoiceInput";
import { ResultSummary } from "./ResultSummary";
import { ResultTable } from "./ResultTable";
import { FEATURE, plural, serialNosKey, summarise, uniqueSerialNos } from "./utils";

const GstRecalculation = (props) => {
    const { roleManagement } = useSelector(state => state.authReducer);
    const { history } = props;
    const [serialNos, setSerialNos] = useState([]);
    const [inputError, setInputError] = useState(false);
    const [loading, setLoading] = useState('');
    const [run, setRun] = useState(null);
    const [filter, setFilter] = useState('ALL');
    const [confirmVisible, setConfirmVisible] = useState(false);

    useEffect(() => {
        if (roleManagement !== '') {
            checkViewPermission(FEATURE, history, 'Sales');
        }
    }, [roleManagement]);

    const canApply = roleManagement !== '' && checkCRUDPermission(FEATURE, 'edit', 'Sales');
    const canPreview = canApply || (roleManagement !== '' && checkCRUDPermission(FEATURE, 'view', 'Sales'));

    const results = run?.response?.results || [];
    const dryRun = !!run?.response?.dryRun;
    const summary = useMemo(() => summarise(results), [run]);
    const changedSerialNos = uniqueSerialNos(results.filter((r) => r.status === 'CHANGED').map((r) => r.serialNo));
    // A preview only stands for the list it was run on; editing the list means previewing again.
    const stale = dryRun && run.key !== serialNosKey(serialNos);
    const visibleResults = filter === 'ALL' ? results : results.filter((r) => r.status === filter);

    const send = async (list, isDryRun) => {
        setLoading(isDryRun ? 'preview' : 'apply');
        try {
            const { data } = await postRequest('salesInvoice/gstRecalculation', { serialNos: list, dryRun: isDryRun });
            setRun({ response: data, key: serialNosKey(serialNos), at: new Date() });
            setFilter('ALL');
            if (!isDryRun) {
                const { counts } = summarise(data.results);
                toast.dismiss();
                if (counts.FAILED) toast.error(`${plural(counts.FAILED, 'invoice')} could not be recalculated. Open the rows for details.`);
                else toast.success(`Recalculated ${plural(counts.CHANGED, 'invoice')}`);
            }
        } catch (error) {
            toast.dismiss();
            toast.error(error?.response?.data?.error?.message || error?.message || 'GST recalculation failed');
        }
        setLoading('');
    };

    const preview = () => {
        if (serialNos.length === 0) {
            setInputError(true);
            return;
        }
        send(serialNos, true);
    };

    const recalculate = () => {
        setConfirmVisible(false);
        send(changedSerialNos, false);
    };

    const clear = () => {
        setSerialNos([]);
        setInputError(false);
        setRun(null);
        setFilter('ALL');
    };

    const classes = useStyles();
    const appClasses = customStyles();
    const busy = loading !== '';
    const previewIsCurrent = dryRun && !stale;

    const renderPlaceholder = () => (
        busy ?
            <div className={classes.placeholder} aria-live="polite">
                <div className={classes.spinner} />
                <span>{loading === 'preview' ? `Previewing ${plural(serialNos.length, 'invoice')}…` : `Recalculating ${plural(changedSerialNos.length, 'invoice')}…`}</span>
            </div> :
            <div className={classes.placeholder}>
                <ol className={classes.steps}>
                    <li className={classes.step}>
                        <span className={classes.stepNumber}>1</span>
                        <b>Add invoices</b>
                        Type or paste the invoice numbers you want to check.
                    </li>
                    <li className={classes.step}>
                        <span className={classes.stepNumber}>2</span>
                        <b>Preview</b>
                        See line amounts, GST, CGST / SGST / IGST and invoice totals before and after. Nothing is saved.
                    </li>
                    <li className={classes.step}>
                        <span className={classes.stepNumber}>3</span>
                        <b>Recalculate</b>
                        {canApply ? 'Save the changes for the invoices that need them.' : 'Saving needs edit permission for GST Recalculation in Role Management.'}
                    </li>
                </ol>
            </div>
    );

    const renderResults = () => (
        <>
            <div className={classes.resultsHeader}>
                <h3 className={classes.resultsTitle}>
                    {dryRun ? 'Preview' : 'Recalculation complete'}
                    <span className={clsx(classes.badge, classes[dryRun ? 'tone_warning' : 'tone_saved'])}>{dryRun ? 'Not saved' : 'Saved'}</span>
                    <span className={classes.resultsTime}>{run.at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </h3>
                {
                    dryRun && (
                        changedSerialNos.length > 0 ?
                            <div className={classes.applyArea}>
                                {
                                    !canApply &&
                                    <span className={classes.note}>Saving needs <b>Edit</b> permission for GST Recalculation in Role Management.</span>
                                }
                                <Button
                                    color="primary"
                                    variant="contained"
                                    disabled={stale || !canApply}
                                    id={idNamingConversion('Recalculate', 'gstRecalculation', 'Button')}
                                    onClick={() => setConfirmVisible(true)}
                                    className={clsx(appClasses.modalBtn, { [classes.buttonDisabled]: stale || !canApply })}>
                                    Recalculate {plural(changedSerialNos.length, 'invoice')}
                                </Button>
                            </div> :
                            <span className={classes.note}>Nothing to save: every invoice is already correct.</span>
                    )
                }
            </div>
            {
                stale &&
                <div className={classes.staleNotice} role="status">
                    <ReportProblemOutlined />
                    The invoice list has changed since this preview. Preview again before recalculating.
                </div>
            }
            <ResultSummary summary={summary} dryRun={dryRun} filter={filter} onFilter={setFilter} classes={classes} />
            <ResultTable key={run.at.getTime()} results={visibleResults} dryRun={dryRun} classes={classes} />
        </>
    );

    return (
        <React.Fragment>
            <MainHeader {...props} cls={'newImg'} img={SalesIcon} title={'Sales'} links={[{ 'label': 'Sales', 'link': '/sales' },
            { 'label': FEATURE, 'link': '' }]} />
            <div className={clsx(appClasses.margin40, appClasses.listTableMargin)}>
                <PrimaryCard
                    title={FEATURE}
                    component={
                        <div className={classes.page}>
                            <div className={classes.intro}>
                                <InfoOutlined />
                                <p>
                                    Re-prices each invoice line from its quantity, price and discount, recalculates its GST (CGST / SGST / IGST) from the
                                    product GST rate and rebuilds the invoice totals, then updates the Sales Bill and GST transaction to match.
                                    {' '}<b>Nothing is saved until you click Recalculate.</b> Customer balances are not adjusted.
                                </p>
                            </div>

                            <section className={classes.panel} aria-labelledby="gstRecalculationInvoices">
                                <div className={classes.panelHeader}>
                                    <h3 id="gstRecalculationInvoices" className={classes.panelTitle}>Invoice numbers</h3>
                                    <div className={classes.panelMeta}>
                                        <span>{plural(serialNos.length, 'invoice')}</span>
                                        <button
                                            type="button"
                                            disabled={serialNos.length === 0 || busy}
                                            onClick={() => clear()}
                                            id={idNamingConversion('Clear All', 'gstRecalculation', 'Button')}
                                            className={classes.textButton}>Clear all</button>
                                    </div>
                                </div>
                                <InvoiceInput
                                    value={serialNos}
                                    onChange={(next) => { setSerialNos(next); setInputError(false); }}
                                    disabled={busy}
                                    error={inputError}
                                    classes={classes} />
                                <div className={classes.panelFooter}>
                                    <p className={clsx(classes.hint, { [classes.hintError]: inputError })}>
                                        {inputError ? 'Add at least one invoice number to preview.' : 'Separate numbers with Enter, comma or space. A column pasted from Excel works too.'}
                                    </p>
                                    {
                                        canPreview &&
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            disabled={busy}
                                            id={idNamingConversion('Preview', 'gstRecalculation', 'Button')}
                                            onClick={() => preview()}
                                            className={clsx(previewIsCurrent ? appClasses.AddMoreBtn : appClasses.modalBtn, { [appClasses.loading]: loading === 'preview' })}>
                                            {previewIsCurrent ? 'Preview again' : 'Preview changes'}
                                        </Button>
                                    }
                                </div>
                            </section>

                            {busy || !run ? renderPlaceholder() : renderResults()}
                        </div>
                    }
                />
            </div>
            <CustomModal
                title={'Recalculate GST'}
                visible={confirmVisible}
                onClose={(v) => setConfirmVisible(v)}
                onOpen={() => recalculate()}
                yesId={idNamingConversion('Confirm Recalculate', 'gstRecalculation', 'Button')}
                noId={idNamingConversion('Cancel Recalculate', 'gstRecalculation', 'Button')}
                yesButton={'Recalculate'}
                noButton={'Cancel'}
                text={
                    <p>
                        Save the recalculation for <b>{plural(changedSerialNos.length, 'invoice')}</b>? This updates the invoice and its lines
                        (amounts, GST, CGST / SGST / IGST), the Sales Bill and the GST transaction. Invoices with no change are skipped, and
                        customer balances are not adjusted.
                    </p>
                }
            />
        </React.Fragment>
    )
}

export default GstRecalculation
